import { LicenseInvoiceDownloadButton } from "@/components/license-details/cards/license-invoice-download-button";
import { DetailsTileTitle } from "@/components/license-details/tile-info";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type { UserType } from "@/constants";
import { getFeesForRenewalOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  LicensePaymentResponseSchema,
  LicensePaymentsResponseSchema,
  NonRegistrantLicensePaymentResponseSchema,
  NonRegistrantLicensePaymentsResponseSchema,
} from "@/hera-api/client/types.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { canDownloadInvoicePdf } from "@/lib/download-invoice-pdf";
import {
  cn,
  formatCurrency,
  getLocalTodayYmd,
  licensePaymentRowHasBillableInvoiceFees,
  normalizeCalendarDateString,
  parseDateAsLocal,
} from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Loader2 } from "lucide-react";
import { useMemo, useState } from "react";

/** License row shape needed to derive inspection-side payments (matches payments card). */
export type LicenseDataForPaymentsDerived = {
  inspections?: Array<{
    id: string;
    effective_date?: string | null;
    effective_end_date?: string | null;
    created_at?: string;
    payments?: Array<{
      fee_paid: string;
      fee_prefilled: string;
      payment_type: string;
      payment_processor_transaction?: {
        status: string;
      } | null;
    }>;
  }>;
};

export type LicensePaymentsDerivedInput = {
  payments:
    | LicensePaymentsResponseSchema
    | NonRegistrantLicensePaymentsResponseSchema;
  userType: UserType;
  licenseId: string;
  licenseData?: LicenseDataForPaymentsDerived;
};

/** Normalize API enum strings (e.g. FEE_ADJUSTMENT, fee-adjustment). */
function normalisePaymentEnumField(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v).trim().toLowerCase().replace(/-/g, "_");
}

function readManualPaymentMethodType(payment: {
  manual_payment_method_type?: string | null;
}): string {
  const raw =
    payment.manual_payment_method_type ??
    (payment as { manualPaymentMethodType?: string | null })
      .manualPaymentMethodType;
  return normalisePaymentEnumField(raw);
}

/** Fee-first split of refunded fee across registration and late (matches backend). */
export function splitRefundedFeeComponents(payment: {
  fee_paid: string;
  late_fee_paid: string;
  refunded_fee_amount?: string | null;
}): { refundedReg: number; refundedLate: number } {
  const feePaid = parseLicenseMoneyField(payment.fee_paid);
  const latePaid = parseLicenseMoneyField(payment.late_fee_paid);
  const refunded = parseLicenseMoneyField(payment.refunded_fee_amount);
  const refundedReg = Math.min(refunded, feePaid);
  const refundedLate = Math.min(Math.max(refunded - feePaid, 0), latePaid);
  return { refundedReg, refundedLate };
}

export function paymentNetSubtotal(payment: {
  fee_paid: string;
  late_fee_paid: string;
  net_paid?: string | null;
}): number {
  if (payment.net_paid != null && payment.net_paid !== "") {
    return parseLicenseMoneyField(payment.net_paid);
  }
  return (
    parseLicenseMoneyField(payment.fee_paid) +
    parseLicenseMoneyField(payment.late_fee_paid)
  );
}

export type PaymentDisplayRow =
  | LicensePaymentResponseSchema
  | NonRegistrantLicensePaymentResponseSchema
  | ((
      | LicensePaymentResponseSchema
      | NonRegistrantLicensePaymentResponseSchema
    ) & {
      _isRefundLine?: boolean;
      _displayPaymentId?: string;
    });

/** Expand processor payments into payment + refund rows when fees were refunded. */
export function expandPaymentRowsForDisplay(
  payment:
    | LicensePaymentResponseSchema
    | NonRegistrantLicensePaymentResponseSchema,
): PaymentDisplayRow[] {
  const refundedTotal = parseLicenseMoneyField(payment.refunded_fee_amount);
  if (
    refundedTotal <= 0 ||
    payment.payment_type?.toLowerCase() !== "payment_processor" ||
    !payment.payment_processor_transaction
  ) {
    return [payment];
  }

  const { refundedReg, refundedLate } = splitRefundedFeeComponents(payment);
  const refundTimestamp =
    payment.payment_processor_transaction.refund_timestamp ?? null;

  const refundRow = {
    ...payment,
    id: `${payment.id}-refund`,
    _isRefundLine: true as const,
    _displayPaymentId: payment.id,
    fee_paid: String(-refundedReg),
    late_fee_paid: String(-refundedLate),
    payment_processor_transaction: {
      ...payment.payment_processor_transaction,
      status: "refunded" as const,
      effective_status: "refunded" as const,
      payment_timestamp:
        refundTimestamp ??
        payment.payment_processor_transaction.payment_timestamp,
      succeeded_at:
        refundTimestamp ?? payment.payment_processor_transaction.succeeded_at,
    },
  };

  const paymentRow = {
    ...payment,
    payment_processor_transaction: payment.payment_processor_transaction
      ? {
          ...payment.payment_processor_transaction,
          status: "succeeded" as const,
          effective_status: "succeeded" as const,
        }
      : payment.payment_processor_transaction,
  };

  return [paymentRow, refundRow];
}

/** Parse fee_paid / late_fee_paid from API (string, number, or odd JSON). */
export function parseLicenseMoneyField(v: unknown): number {
  if (v == null || v === "") return 0;
  if (typeof v === "number" && Number.isFinite(v)) {
    return v;
  }
  const n = Number.parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
}

/** Operator fee adjustments (not real registration/late fee cash flow). */
export function isFeeAdjustmentLicensePayment(payment: {
  payment_type?: string | null;
  manual_payment_method_type?: string | null;
}): boolean {
  return readManualPaymentMethodType(payment) === "fee_adjustment";
}

/**
 * Processor statuses that must not count toward Fee Paid in period compliance.
 * Matches backend fee math: only succeeded (and similar terminal) credits apply.
 */
export function isIncompletePaymentProcessorStatus(
  status: string | null | undefined,
): boolean {
  return status === "pending" || status === "processing";
}

function countsTowardPeriodPaidAmount(payment: {
  payment_type?: string | null;
  payment_processor_transaction?: { status?: string | null } | null;
}): boolean {
  if (
    payment.payment_type?.toLowerCase() === "payment_processor" &&
    !payment.payment_processor_transaction
  ) {
    return false;
  }
  const status = payment.payment_processor_transaction?.status;
  return !isIncompletePaymentProcessorStatus(status);
}

/** Registration + late paid subtotal for UI totals, excluding fee-adjustment rows. */
export function licensePaymentRowsSubtotalExcludingFeeAdjustments(
  payments: ReadonlyArray<{
    fee_paid: string;
    late_fee_paid: string;
    payment_type?: string | null;
    manual_payment_method_type?: string | null;
  }>,
): number {
  return payments.reduce((sum, p) => {
    if (isFeeAdjustmentLicensePayment(p)) {
      return sum;
    }
    return sum + paymentNetSubtotal(p);
  }, 0);
}

/** Format API calendar date (YYYY-MM-DD) without UTC midnight shift. */
export function formatPeriodDateLabel(isoDate: string): string {
  const normalized = normalizeCalendarDateString(isoDate) ?? isoDate;
  const d = parseDateAsLocal(normalized);
  if (!d) return isoDate;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function calendarDayDifference(
  earlier: string | null | undefined,
  later: string | null | undefined,
): number | null {
  const a = earlier ? parseDateAsLocal(earlier) : null;
  const b = later ? parseDateAsLocal(later) : null;
  if (!a || !b) return null;
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

function bestPeriodEndFromPaymentGroup(group: PaymentGroup): string | null {
  let bestEndIso: string | null = null;
  let bestEndTime = -1;
  for (const payment of group.payments) {
    const end = normalizeCalendarDateString(payment.period_end_date);
    if (!end) continue;
    const time = parseDateAsLocal(end)?.getTime() ?? -1;
    if (time > bestEndTime) {
      bestEndTime = time;
      bestEndIso = end;
    }
  }
  return bestEndIso;
}

export type RenewalFeePeriodRow = {
  start_date: string;
  end_date?: string | null;
  registration_fee?: string | null;
  late_fee?: string | null;
  original_registration_fee?: string | null;
  accrued_late_fees?: Array<string | number> | null;
  accrued_late_fee_dates?: Array<string> | null;
};

/** Match a payment row's period to a renewal fee row (including one-day shift). */
export function findFeeForPaymentPeriod(
  periodStart: string | null | undefined,
  periodEnd: string | null | undefined,
  feesPayload?: RenewalFeesPayload,
): RenewalFeePeriodRow | undefined {
  const fees = feesPayload?.fees ?? [];
  if (fees.length === 0) return undefined;

  const payStart = normalizeCalendarDateString(periodStart);
  const payEnd = normalizeCalendarDateString(periodEnd);

  if (payStart) {
    const byStart = fees.find(
      (fee) => normalizeCalendarDateString(fee.start_date) === payStart,
    );
    if (byStart) return byStart;
  }

  if (payEnd) {
    const byEnd = fees.find(
      (fee) => normalizeCalendarDateString(fee.end_date) === payEnd,
    );
    if (byEnd) return byEnd;
  }

  if (payStart && payEnd) {
    for (const fee of fees) {
      const feeStart = normalizeCalendarDateString(fee.start_date);
      const feeEnd = normalizeCalendarDateString(fee.end_date);
      if (
        calendarDayDifference(payStart, feeStart) === 1 &&
        calendarDayDifference(payEnd, feeEnd) === 1
      ) {
        return fee;
      }
    }
  }

  return undefined;
}

function findFeeForPaymentGroup(
  group: PaymentGroup,
  feesPayload: RenewalFeesPayload,
): RenewalFeePeriodRow | undefined {
  return findFeeForPaymentPeriod(
    group.payments[0]?.period_start_date,
    bestPeriodEndFromPaymentGroup(group),
    feesPayload,
  );
}

export function resolveCanonicalPeriodDatesForPaymentRow(
  periodStart: string,
  periodEnd: string | null | undefined,
  feesPayload?: RenewalFeesPayload,
): { start: string; end: string | null } {
  const payStart = normalizeCalendarDateString(periodStart) ?? "";
  const payEnd = normalizeCalendarDateString(periodEnd);
  const fee = feesPayload
    ? findFeeForPaymentPeriod(periodStart, periodEnd, feesPayload)
    : undefined;

  if (fee) {
    return {
      start: normalizeCalendarDateString(fee.start_date) ?? payStart,
      end: normalizeCalendarDateString(fee.end_date) ?? payEnd,
    };
  }

  return { start: payStart, end: payEnd };
}

/** MM/DD/YYYY for API calendar dates (local, not UTC-shifted). */
export function formatPaymentCalendarUsDate(isoDate: string): string {
  const normalized = normalizeCalendarDateString(isoDate) ?? isoDate;
  const d = parseDateAsLocal(normalized);
  if (!d) return isoDate;
  return new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }).format(d);
}

/** Period range label for a single payment row, aligned to renewal fees when available. */
export function formatPaymentPeriodRangeLabel(
  periodStart: string,
  periodEnd: string | null | undefined,
  feesPayload?: RenewalFeesPayload,
): string {
  const { start, end } = resolveCanonicalPeriodDatesForPaymentRow(
    periodStart,
    periodEnd,
    feesPayload,
  );
  if (!start) return "";
  const left = formatPeriodDateLabel(start);
  if (!end) return `${left} - Ongoing`;
  return `${left} - ${formatPeriodDateLabel(end)}`;
}

/** US short period range (MM/DD/YYYY), aligned to renewal fees when available. */
export function formatPaymentPeriodUsRangeLabel(
  periodStart: string,
  periodEnd: string | null | undefined,
  feesPayload?: RenewalFeesPayload,
): string {
  const { start, end } = resolveCanonicalPeriodDatesForPaymentRow(
    periodStart,
    periodEnd,
    feesPayload,
  );
  if (!start) return "";
  const left = formatPaymentCalendarUsDate(start);
  if (!end) return left;
  return `${left} – ${formatPaymentCalendarUsDate(end)}`;
}

export function findComplianceSectionForPaymentGroup(
  group: PaymentGroup,
  complianceSections: RegistrationPeriodComplianceSection[],
): RegistrationPeriodComplianceSection | undefined {
  if (group.payments.length === 0 || complianceSections.length === 0) {
    return undefined;
  }

  const payStart = normalizeCalendarDateString(
    group.payments[0]?.period_start_date,
  );
  if (!payStart) return undefined;

  const exact = complianceSections.find(
    (section) => normalizeCalendarDateString(section.periodStart) === payStart,
  );
  if (exact) return exact;

  const payEnd = bestPeriodEndFromPaymentGroup(group);
  for (const section of complianceSections) {
    const startDiff = calendarDayDifference(payStart, section.periodStart);
    if (startDiff !== 1) continue;
    if (!payEnd || !section.periodEnd) {
      return section;
    }
    if (calendarDayDifference(payEnd, section.periodEnd) === 1) {
      return section;
    }
  }

  return undefined;
}

function resolveCanonicalPeriodDatesForPaymentGroup(
  group: PaymentGroup,
  feesPayload?: RenewalFeesPayload,
): { start: string; end: string | null } {
  const payStart =
    normalizeCalendarDateString(group.payments[0]?.period_start_date) ?? "";
  const payEnd = bestPeriodEndFromPaymentGroup(group);
  const fee = feesPayload
    ? findFeeForPaymentGroup(group, feesPayload)
    : undefined;

  if (fee) {
    return {
      start: normalizeCalendarDateString(fee.start_date) ?? payStart,
      end: normalizeCalendarDateString(fee.end_date) ?? payEnd,
    };
  }

  return { start: payStart, end: payEnd };
}

export interface PaymentGroup {
  payments: (
    | LicensePaymentResponseSchema
    | NonRegistrantLicensePaymentResponseSchema
  )[];
  totalAmount: number;
}

export type RenewalFeesPayload =
  | {
      fees?: RenewalFeePeriodRow[];
    }
  | undefined;

export type PeriodSummaryTotals = {
  totalRegFee: number;
  totalLateFee: number;
  totalRegPaid: number;
  totalLatePaid: number;
  totalRegAdjustment: number;
  totalLateAdjustment: number;
  originalRegistrationFee: number;
  everLateFees: LateFeeInstallmentView[];
  regDue: number;
  lateDue: number;
  totalDue: number;
};

/** One registration renewal period: paid vs due (compliance) figures. */
export type RegistrationPeriodComplianceSection = {
  /** Raw period start (ISO date string from API / payments). */
  periodStart: string;
  /** Canonical period end from fees or payments. */
  periodEnd: string | null;
  /** Display label for period start (local calendar date). */
  periodStartLabel: string;
  /** Display label for period end, or "Ongoing" when open-ended. */
  periodEndLabel: string;
  summary: PeriodSummaryTotals;
  isCurrentPeriod: boolean;
  hasPayments: boolean;
  /** Open invoice / finalized invoice manual transactions for this period (HTML download). */
  invoiceDownloadTargets: Array<{ manualTransactionId: string }>;
};

/**
 * Group key for payments list: one block per renewal period (license), or per
 * inspection when the row is inspection-derived (avoids merging different inspections
 * that share the same effective date).
 */
function paymentListGroupKey(
  payment:
    | LicensePaymentResponseSchema
    | NonRegistrantLicensePaymentResponseSchema,
): string {
  const inspectionId = (payment as { _inspectionId?: string })._inspectionId;
  const start = payment.period_start_date?.trim();
  if (inspectionId) {
    return `${start || "unknown"}#inspection:${inspectionId}`;
  }
  return start || "unknown_period";
}

export type PaymentGroupPeriodLabelOptions = {
  complianceSections?: RegistrationPeriodComplianceSection[];
  feesPayload?: RenewalFeesPayload;
};

/** Period heading for a payment group (merged license rows share one renewal start). */
export function formatPaymentGroupPeriodRangeLabel(
  group: PaymentGroup,
  options?: PaymentGroupPeriodLabelOptions,
): string {
  if (group.payments.length === 0) return "";

  const complianceSection = options?.complianceSections
    ? findComplianceSectionForPaymentGroup(group, options.complianceSections)
    : undefined;
  if (complianceSection) {
    return `${complianceSection.periodStartLabel} - ${complianceSection.periodEndLabel}`;
  }

  const { start, end } = resolveCanonicalPeriodDatesForPaymentGroup(
    group,
    options?.feesPayload,
  );
  if (!start) return "";

  const left = formatPeriodDateLabel(start);
  if (!end) {
    return `${left} - Ongoing`;
  }
  return `${left} - ${formatPeriodDateLabel(end)}`;
}

export function sortAndGroupPayments(
  paymentList: (
    | LicensePaymentResponseSchema
    | NonRegistrantLicensePaymentResponseSchema
  )[],
): Record<string, PaymentGroup> {
  const sorted = [...paymentList].sort((a, b) => {
    const dateA = parseDateAsLocal(a.period_start_date);
    const dateB = parseDateAsLocal(b.period_start_date);
    const dateComparison = (dateB?.getTime() ?? 0) - (dateA?.getTime() ?? 0);

    if (dateComparison === 0) {
      return (
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    }

    return dateComparison;
  });

  return sorted.reduce(
    (acc, payment) => {
      const key = paymentListGroupKey(payment);
      if (!acc[key]) {
        acc[key] = {
          payments: [],
          totalAmount: 0,
        };
      }
      acc[key].payments.push(payment);
      if (!isFeeAdjustmentLicensePayment(payment)) {
        acc[key].totalAmount += paymentNetSubtotal(payment);
      }
      return acc;
    },
    {} as Record<string, PaymentGroup>,
  );
}

function buildInspectionPaymentsFromLicense(
  licenseData: LicenseDataForPaymentsDerived | undefined,
): (
  | LicensePaymentResponseSchema
  | NonRegistrantLicensePaymentResponseSchema
)[] {
  const inspectionPayments: (
    | LicensePaymentResponseSchema
    | NonRegistrantLicensePaymentResponseSchema
  )[] = [];
  if (!licenseData?.inspections) return inspectionPayments;

  for (const inspection of licenseData.inspections) {
    const inspectionPaymentsList = (inspection as { payments?: unknown[] })
      .payments;
    if (!inspectionPaymentsList || !Array.isArray(inspectionPaymentsList)) {
      continue;
    }
    for (const inspectionPayment of inspectionPaymentsList) {
      const ip = inspectionPayment as {
        payment_type?: string;
        payment_processor_transaction?: { status?: string } | null;
        id?: string;
        created_at?: string;
        fee_paid?: string;
        fee_prefilled?: string;
        manual_payment_date?: string | null;
        manual_payment_method_type?: string | null;
        check_id?: string | null;
        manual_payment_check_id?: string | null;
        check_number?: string | null;
        manual_transaction_status?: string | null;
        processing_fee?: string;
      };
      if (
        ip.payment_type?.toLowerCase() === "payment_processor" &&
        !ip.payment_processor_transaction
      ) {
        continue;
      }
      if (ip.payment_processor_transaction?.status === "pending") {
        continue;
      }

      let periodDate = inspection.effective_date;
      if (!periodDate && inspection.created_at) {
        periodDate = inspection.created_at.split("T")[0];
      }
      if (!periodDate) {
        periodDate = getLocalTodayYmd();
      }

      const periodEndDate = inspection.effective_end_date;

      const paymentId = ip.id || `inspection-${inspection.id}-${Date.now()}`;

      const paymentProcessorTransaction = ip.payment_processor_transaction
        ? { ...ip.payment_processor_transaction }
        : null;

      const rawCheckId = ip.check_id ?? ip.manual_payment_check_id ?? null;

      inspectionPayments.push({
        id: paymentId,
        created_at:
          ip.created_at || inspection.created_at || new Date().toISOString(),
        payment_processor_transaction:
          paymentProcessorTransaction as NonRegistrantLicensePaymentResponseSchema["payment_processor_transaction"],
        payment_type:
          ip.payment_type as NonRegistrantLicensePaymentResponseSchema["payment_type"],
        fee_paid: ip.fee_paid || "0",
        fee_prefilled: ip.fee_prefilled || "0",
        late_fee_prefilled: "0",
        late_fee_paid: "0",
        period_start_date: periodDate,
        period_end_date: periodEndDate,
        comment: null,
        selected: null,
        manual_payment_date: ip.manual_payment_date ?? null,
        manual_payment_method_type: ip.manual_payment_method_type ?? null,
        check_id: rawCheckId,
        check_number: ip.check_number ?? null,
        manual_transaction_status: ip.manual_transaction_status ?? null,
        processing_fee: ip.processing_fee || "0",
        _isInspectionPayment: true,
        _inspectionId: inspection.id,
      } as NonRegistrantLicensePaymentResponseSchema & {
        _isInspectionPayment?: boolean;
        _inspectionId?: string;
      });
    }
  }
  return inspectionPayments;
}

function filterLicensePayments(
  payments: LicensePaymentsResponseSchema["payments"],
) {
  return payments.filter((payment) => {
    if (
      payment.payment_type?.toLowerCase() === "manual_adjustment" &&
      payment.manual_transaction_status?.toLowerCase() === "cancelled"
    ) {
      return false;
    }
    if (
      payment.payment_type?.toLowerCase() === "payment_processor" &&
      !payment.payment_processor_transaction
    ) {
      return false;
    }
    if (payment.payment_processor_transaction?.status === "pending") {
      return false;
    }
    return true;
  });
}

function filterInspectionPayments(
  inspectionPayments: (
    | LicensePaymentResponseSchema
    | NonRegistrantLicensePaymentResponseSchema
  )[],
) {
  return inspectionPayments.filter((payment) => {
    if (
      payment.payment_type?.toLowerCase() === "manual_adjustment" &&
      payment.manual_transaction_status?.toLowerCase() === "cancelled"
    ) {
      return false;
    }
    if (
      payment.payment_type?.toLowerCase() === "payment_processor" &&
      !payment.payment_processor_transaction
    ) {
      return false;
    }
    if (payment.payment_processor_transaction?.status === "pending") {
      return false;
    }
    return true;
  });
}

export function isCurrentPeriod(
  periodStartDate: string,
  periodEndDate: string | null,
) {
  if (!periodEndDate) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = parseDateAsLocal(periodStartDate);
  if (!start) return false;
  start.setHours(0, 0, 0, 0);
  const end = parseDateAsLocal(periodEndDate);
  if (!end) return false;
  end.setHours(0, 0, 0, 0);
  if (start > today) return false;
  return today >= start && today <= end;
}

function paymentNetRegAndLate(payment: {
  fee_paid: string;
  late_fee_paid: string;
  net_paid?: string | null;
  refunded_fee_amount?: string | null;
}): { reg: number; late: number } {
  const net = paymentNetSubtotal(payment);
  const feePaid = parseLicenseMoneyField(payment.fee_paid);
  const latePaid = parseLicenseMoneyField(payment.late_fee_paid);
  const gross = feePaid + latePaid;
  if (gross <= 0) return { reg: 0, late: 0 };
  return {
    reg: (net * feePaid) / gross,
    late: (net * latePaid) / gross,
  };
}

/** One accrued late-fee installment, with the date it hit when the API supplies it. */
export type LateFeeInstallmentView = {
  amount: number;
  accruedOn: string | null;
};

function parseAccruedLateFeeInstallments(
  rawAmounts: unknown,
  rawDates: unknown,
): LateFeeInstallmentView[] {
  if (!Array.isArray(rawAmounts)) return [];
  const dates = Array.isArray(rawDates) ? rawDates : [];
  return rawAmounts
    .map((value, index) => {
      const rawDate = dates[index];
      return {
        amount: parseLicenseMoneyField(String(value)),
        accruedOn:
          typeof rawDate === "string"
            ? normalizeCalendarDateString(rawDate)
            : null,
      };
    })
    .filter((installment) => installment.amount > 0);
}

function roundMoney(amount: number): number {
  return Math.round(amount * 100) / 100;
}

export function calculatePeriodSummary(
  payments: (
    | LicensePaymentResponseSchema
    | NonRegistrantLicensePaymentResponseSchema
  )[],
  periodStartDate: string | null,
  feesPayload: RenewalFeesPayload,
): PeriodSummaryTotals {
  const compliancePayments = payments.filter(
    (p) => !isFeeAdjustmentLicensePayment(p),
  );

  const nonPendingPayments = compliancePayments.filter(
    countsTowardPeriodPaidAmount,
  );

  const totalRegPaid = nonPendingPayments.reduce(
    (sum, p) => sum + paymentNetRegAndLate(p).reg,
    0,
  );
  const totalLatePaid = nonPendingPayments.reduce(
    (sum, p) => sum + paymentNetRegAndLate(p).late,
    0,
  );

  const adjustmentPayments = payments.filter(
    (p) => isFeeAdjustmentLicensePayment(p) && countsTowardPeriodPaidAmount(p),
  );
  const totalRegAdjustment = adjustmentPayments.reduce(
    (sum, p) => sum + paymentNetRegAndLate(p).reg,
    0,
  );
  const totalLateAdjustment = adjustmentPayments.reduce(
    (sum, p) => sum + paymentNetRegAndLate(p).late,
    0,
  );

  const matchingFee = periodStartDate
    ? feesPayload?.fees?.find((fee) => fee.start_date === periodStartDate)
    : null;

  let regDue = 0;
  let lateDue = 0;
  let totalRegFee = 0;
  let totalLateFee = 0;

  if (matchingFee) {
    regDue = Number.parseFloat(matchingFee.registration_fee || "0");
    lateDue = Number.parseFloat(matchingFee.late_fee || "0");
    totalRegFee = totalRegPaid + totalRegAdjustment + regDue;
    totalLateFee = totalLatePaid + totalLateAdjustment + lateDue;
  } else if (compliancePayments.length > 0) {
    const prefilledPayment = compliancePayments.find(
      (p) =>
        p.payment_type?.toLowerCase() === "payment_processor" &&
        (Number.parseFloat(p.fee_prefilled) > 0 ||
          Number.parseFloat(p.late_fee_prefilled) > 0),
    );

    if (prefilledPayment) {
      totalRegFee = Number.parseFloat(prefilledPayment.fee_prefilled);
      totalLateFee = Number.parseFloat(prefilledPayment.late_fee_prefilled);
    } else {
      const allRegFees = compliancePayments.map((p) =>
        parseLicenseMoneyField(p.fee_paid),
      );
      const allLateFees = compliancePayments.map((p) =>
        parseLicenseMoneyField(p.late_fee_paid),
      );
      totalRegFee = Math.max(...allRegFees, 0);
      totalLateFee = Math.max(...allLateFees, 0);
    }

    const paidExcludingIncomplete = compliancePayments
      .filter(countsTowardPeriodPaidAmount)
      .reduce(
        (acc, p) => ({
          reg: acc.reg + parseLicenseMoneyField(p.fee_paid),
          late: acc.late + parseLicenseMoneyField(p.late_fee_paid),
        }),
        { reg: 0, late: 0 },
      );

    regDue = Math.max(0, totalRegFee - paidExcludingIncomplete.reg);
    lateDue = Math.max(0, totalLateFee - paidExcludingIncomplete.late);
  }

  const totalDue = roundMoney(regDue + lateDue);

  const apiOriginalRegistrationFee = matchingFee
    ? parseLicenseMoneyField(matchingFee.original_registration_fee)
    : 0;
  const originalRegistrationFee = roundMoney(
    apiOriginalRegistrationFee > 0
      ? apiOriginalRegistrationFee
      : matchingFee
        ? totalRegPaid + totalRegAdjustment + regDue
        : totalRegFee,
  );

  const apiEverLateFees = matchingFee
    ? parseAccruedLateFeeInstallments(
        matchingFee.accrued_late_fees,
        matchingFee.accrued_late_fee_dates,
      )
    : [];
  const reconstructedEverLate = roundMoney(
    matchingFee ? totalLatePaid + totalLateAdjustment + lateDue : lateDue,
  );
  const everLateFees: LateFeeInstallmentView[] =
    apiEverLateFees.length > 0
      ? apiEverLateFees.map((installment) => ({
          ...installment,
          amount: roundMoney(installment.amount),
        }))
      : reconstructedEverLate > 0
        ? [{ amount: reconstructedEverLate, accruedOn: null }]
        : [];

  return {
    totalRegFee,
    totalLateFee,
    totalRegPaid: roundMoney(totalRegPaid),
    totalLatePaid: roundMoney(totalLatePaid),
    totalRegAdjustment: roundMoney(totalRegAdjustment),
    totalLateAdjustment: roundMoney(totalLateAdjustment),
    originalRegistrationFee,
    everLateFees,
    regDue: roundMoney(regDue),
    lateDue: roundMoney(lateDue),
    totalDue,
  };
}

function collectInvoiceDownloadTargetsForPeriod(
  paymentsForPeriod: (
    | LicensePaymentResponseSchema
    | NonRegistrantLicensePaymentResponseSchema
  )[],
): Array<{ manualTransactionId: string }> {
  const billableByMt = new Map<string, boolean>();
  for (const p of paymentsForPeriod) {
    if (p.payment_type?.toLowerCase() !== "manual_adjustment") {
      continue;
    }
    if ((p.manual_payment_method_type ?? "").toLowerCase() !== "invoice") {
      continue;
    }
    const st = (p.manual_transaction_status ?? "").toLowerCase();
    if (st !== "awaiting_payment" && st !== "finalised") {
      continue;
    }
    const id = p.manual_transaction_id;
    if (!id) {
      continue;
    }
    if (licensePaymentRowHasBillableInvoiceFees(p)) {
      billableByMt.set(id, true);
    }
  }

  const seen = new Set<string>();
  const out: Array<{ manualTransactionId: string }> = [];
  for (const p of paymentsForPeriod) {
    if (p.payment_type?.toLowerCase() !== "manual_adjustment") {
      continue;
    }
    if ((p.manual_payment_method_type ?? "").toLowerCase() !== "invoice") {
      continue;
    }
    const st = (p.manual_transaction_status ?? "").toLowerCase();
    if (st !== "awaiting_payment" && st !== "finalised") {
      continue;
    }
    const id = p.manual_transaction_id;
    if (!id || seen.has(id)) {
      continue;
    }
    if (!billableByMt.get(id)) {
      continue;
    }
    seen.add(id);
    out.push({ manualTransactionId: id });
  }
  return out;
}

function resolveCanonicalCompliancePeriodStart(
  paymentStart: string,
  feesPayload: RenewalFeesPayload,
): string {
  const normalized = normalizeCalendarDateString(paymentStart);
  if (!normalized) return paymentStart;

  for (const fee of feesPayload?.fees ?? []) {
    const feeStart = normalizeCalendarDateString(fee.start_date);
    if (!feeStart) continue;
    if (feeStart === normalized) {
      return fee.start_date;
    }
    if (calendarDayDifference(normalized, fee.start_date) === 1) {
      return fee.start_date;
    }
  }

  return paymentStart;
}

function paymentBelongsToCompliancePeriod(
  payment:
    | LicensePaymentResponseSchema
    | NonRegistrantLicensePaymentResponseSchema,
  periodStart: string,
  feesPayload: RenewalFeesPayload,
): boolean {
  const payStart = normalizeCalendarDateString(payment.period_start_date);
  const periodStartNorm = normalizeCalendarDateString(periodStart);
  if (!payStart || !periodStartNorm) {
    return payment.period_start_date === periodStart;
  }
  if (payStart === periodStartNorm) {
    return true;
  }

  const feeRow = feesPayload?.fees?.find(
    (fee) => normalizeCalendarDateString(fee.start_date) === periodStartNorm,
  );
  return Boolean(feeRow && calendarDayDifference(payStart, periodStart) === 1);
}

/** Paid/due summary for every registration period (union of payments + renewal fee rows), newest first. */
export function buildAllRegistrationPeriodComplianceSections(
  groupedLicensePayments: Record<string, PaymentGroup>,
  filteredLicensePayments: (
    | LicensePaymentResponseSchema
    | NonRegistrantLicensePaymentResponseSchema
  )[],
  feesPayload: RenewalFeesPayload,
): RegistrationPeriodComplianceSection[] {
  const metaByStart = new Map<string, { periodEnd: string | null }>();

  for (const periodKey of Object.keys(groupedLicensePayments)) {
    const g = groupedLicensePayments[periodKey];
    if (g.payments.length === 0) continue;
    const rawStart = g.payments[0].period_start_date;
    const start = resolveCanonicalCompliancePeriodStart(rawStart, feesPayload);
    const feeForStart = feesPayload?.fees?.find(
      (fee) =>
        normalizeCalendarDateString(fee.start_date) ===
        normalizeCalendarDateString(start),
    );
    const end =
      feeForStart?.end_date ??
      bestPeriodEndFromPaymentGroup(g) ??
      g.payments[0].period_end_date ??
      null;
    const prev = metaByStart.get(start);
    if (!prev) {
      metaByStart.set(start, { periodEnd: end });
    } else if (end) {
      prev.periodEnd = end;
    }
  }

  for (const fee of feesPayload?.fees ?? []) {
    const prev = metaByStart.get(fee.start_date);
    if (!prev) {
      metaByStart.set(fee.start_date, { periodEnd: fee.end_date ?? null });
    } else if (fee.end_date) {
      prev.periodEnd = fee.end_date;
    }
  }

  if (metaByStart.size === 0) return [];

  const sortedStarts = [...metaByStart.keys()].sort((a, b) => {
    const ta = parseDateAsLocal(a)?.getTime() ?? 0;
    const tb = parseDateAsLocal(b)?.getTime() ?? 0;
    return tb - ta;
  });

  const sections: RegistrationPeriodComplianceSection[] = [];
  for (const periodStart of sortedStarts) {
    const meta = metaByStart.get(periodStart);
    if (!meta) continue;
    const paymentsForPeriod = filteredLicensePayments.filter((p) =>
      paymentBelongsToCompliancePeriod(p, periodStart, feesPayload),
    );
    const feeRow = feesPayload?.fees?.find(
      (f) =>
        normalizeCalendarDateString(f.start_date) ===
        normalizeCalendarDateString(periodStart),
    );

    const summary = calculatePeriodSummary(
      paymentsForPeriod,
      periodStart,
      feesPayload,
    );

    const resolvedPeriodEnd =
      meta.periodEnd ??
      feeRow?.end_date ??
      paymentsForPeriod[0]?.period_end_date ??
      null;

    sections.push({
      periodStart,
      periodEnd: resolvedPeriodEnd,
      periodStartLabel: formatPeriodDateLabel(periodStart),
      periodEndLabel: resolvedPeriodEnd
        ? formatPeriodDateLabel(resolvedPeriodEnd)
        : "Ongoing",
      summary,
      isCurrentPeriod:
        resolvedPeriodEnd !== null &&
        isCurrentPeriod(periodStart, resolvedPeriodEnd),
      hasPayments: paymentsForPeriod.length > 0,
      invoiceDownloadTargets:
        collectInvoiceDownloadTargetsForPeriod(paymentsForPeriod),
    });
  }

  return sections;
}

export type LicensePaymentsDerived = {
  groupedLicensePayments: Record<string, PaymentGroup>;
  groupedInspectionPayments: Record<string, PaymentGroup>;
  /** Registration fee compliance by period (most recent first). */
  periodComplianceSections: RegistrationPeriodComplianceSection[];
  /** Renewal fee rows used to canonicalize period labels. */
  renewalFees: RenewalFeesPayload;
};

export function useLicensePaymentsDerived({
  payments,
  userType: _userType,
  licenseId,
  licenseData,
}: LicensePaymentsDerivedInput): LicensePaymentsDerived {
  const { isAuthSetupComplete } = useAuthQuerySetup();

  const feesQuery = useQuery({
    ...getFeesForRenewalOptions({
      path: { license_id: licenseId },
    }),
    enabled: isAuthSetupComplete && !!licenseId,
  });

  const inspectionPayments = useMemo(
    () => buildInspectionPaymentsFromLicense(licenseData),
    [licenseData],
  );

  const filteredLicensePayments = useMemo(
    () => filterLicensePayments(payments.payments),
    [payments.payments],
  );

  const groupedComplianceLicensePayments = useMemo(
    () => sortAndGroupPayments(filteredLicensePayments),
    [filteredLicensePayments],
  );

  const filteredInspectionPayments = useMemo(
    () => filterInspectionPayments(inspectionPayments),
    [inspectionPayments],
  );

  const groupedLicensePayments = useMemo(
    () => sortAndGroupPayments(filteredLicensePayments),
    [filteredLicensePayments],
  );

  const groupedInspectionPayments = useMemo(
    () => sortAndGroupPayments(filteredInspectionPayments),
    [filteredInspectionPayments],
  );

  const periodComplianceSections = useMemo(
    () =>
      buildAllRegistrationPeriodComplianceSections(
        groupedComplianceLicensePayments,
        filteredLicensePayments,
        feesQuery.data,
      ),
    [groupedComplianceLicensePayments, filteredLicensePayments, feesQuery.data],
  );

  return {
    groupedLicensePayments,
    groupedInspectionPayments,
    periodComplianceSections,
    renewalFees: feesQuery.data,
  };
}

export type PeriodSummaryFigureRow = {
  label: string;
  kind: "charge" | "total" | "credit" | "balance";
  /** Registration-fee column; null means the cell is blank. */
  registrationAmount: number | null;
  /** Late-fee column; null means the cell is blank. */
  lateAmount: number | null;
};

/**
 * Fixed amount columns, because the header and every figure row are separate
 * grids: content-sized tracks would resolve per row and leave the headings
 * hanging over the wrong numbers.
 */
const PERIOD_SUMMARY_AMOUNT_GRID =
  "grid grid-cols-[minmax(0,1fr)_7rem_7rem] items-center gap-x-3";

function formatPeriodSummaryColumnAmount(amount: number | null): string {
  if (amount == null) return "";
  return formatCurrency(amount);
}

export function collapsedPeriodHeaderAmount(
  summary: PeriodSummaryTotals,
): number {
  if (summary.totalDue > 0) {
    return summary.totalDue;
  }
  return roundMoney(summary.totalRegPaid + summary.totalLatePaid);
}

export function buildPeriodSummaryFigureRows(
  summary: PeriodSummaryTotals,
): PeriodSummaryFigureRow[] {
  const paidRow: PeriodSummaryFigureRow = {
    label: "Fee Paid",
    kind: "credit",
    registrationAmount: summary.totalRegPaid,
    lateAmount: summary.totalLatePaid,
  };
  const adjustmentRow: PeriodSummaryFigureRow = {
    label: "Fee Adjustment",
    kind: "credit",
    registrationAmount: summary.totalRegAdjustment,
    lateAmount: summary.totalLateAdjustment,
  };
  const balanceRow: PeriodSummaryFigureRow = {
    label: "Balance Due",
    kind: "balance",
    registrationAmount: summary.regDue,
    lateAmount: summary.lateDue,
  };

  if (summary.totalDue <= 0) {
    return [paidRow, balanceRow];
  }

  const lateChargeTotal = roundMoney(
    summary.everLateFees.reduce(
      (sum, installment) => sum + installment.amount,
      0,
    ),
  );

  return [
    {
      label: "Registration Fee",
      kind: "charge",
      registrationAmount: summary.originalRegistrationFee,
      lateAmount: null,
    },
    ...summary.everLateFees.map((installment) => ({
      label: installment.accruedOn
        ? `Late Fee (${formatPeriodDateLabel(installment.accruedOn)})`
        : "Late Fee",
      kind: "charge" as const,
      registrationAmount: null,
      lateAmount: installment.amount,
    })),
    {
      label: "Total",
      kind: "total",
      registrationAmount: summary.originalRegistrationFee,
      lateAmount: lateChargeTotal,
    },
    paidRow,
    adjustmentRow,
    balanceRow,
  ];
}

function PeriodSummaryAmountRow({
  row,
  className,
  amountClassName,
}: {
  row: PeriodSummaryFigureRow;
  className?: string;
  amountClassName?: string;
}) {
  return (
    <div className={cn(PERIOD_SUMMARY_AMOUNT_GRID, className)}>
      <span>{row.label}:</span>
      <span className={cn("text-right tabular-nums", amountClassName)}>
        {formatPeriodSummaryColumnAmount(row.registrationAmount)}
      </span>
      <span className={cn("text-right tabular-nums", amountClassName)}>
        {formatPeriodSummaryColumnAmount(row.lateAmount)}
      </span>
    </div>
  );
}

function PeriodSummaryFigures({ summary }: { summary: PeriodSummaryTotals }) {
  const rows = buildPeriodSummaryFigureRows(summary);
  const hasBalanceDue = summary.totalDue > 0;
  const chargeRows = rows.filter((row) => row.kind === "charge");
  const totalRow = rows.find((row) => row.kind === "total");
  const creditRows = rows.filter((row) => row.kind === "credit");
  const balanceRow = rows.find((row) => row.kind === "balance");
  const balanceAmount =
    (balanceRow?.registrationAmount ?? 0) + (balanceRow?.lateAmount ?? 0);

  return (
    <div className="space-y-3 bg-muted/30 p-3 rounded-lg">
      <div
        className={cn(
          PERIOD_SUMMARY_AMOUNT_GRID,
          "text-xs font-medium text-muted-foreground",
        )}
      >
        {/* Real cell, so the two headings stay over their own columns. */}
        <span>
          <span className="sr-only">Fee type</span>
        </span>
        <span className="text-right">Registration Fee</span>
        <span className="text-right">Late Fee</span>
      </div>

      {hasBalanceDue && chargeRows.length > 0 ? (
        <div className="space-y-1">
          {chargeRows.map((row, index) => (
            <PeriodSummaryAmountRow
              key={`${row.label}-${index}`}
              row={row}
              className="text-sm font-semibold"
            />
          ))}
          {totalRow ? (
            <PeriodSummaryAmountRow
              row={totalRow}
              className="pt-1 text-sm font-bold"
            />
          ) : null}
        </div>
      ) : null}

      <div
        className={cn(
          "space-y-1",
          hasBalanceDue &&
            chargeRows.length > 0 &&
            "pt-2 border-t border-border",
        )}
      >
        {creditRows.map((row) => (
          <PeriodSummaryAmountRow
            key={row.label}
            row={row}
            className="text-sm font-semibold"
          />
        ))}
      </div>

      {balanceRow ? (
        <div className="space-y-1 pt-2 border-t border-border">
          <PeriodSummaryAmountRow
            row={balanceRow}
            className="text-base font-bold"
            amountClassName={
              balanceAmount > 0 ? "text-destructive" : "text-green-600"
            }
          />
        </div>
      ) : null}
    </div>
  );
}

function CollapsedPeriodAmount({
  summary,
}: {
  summary: PeriodSummaryTotals;
}) {
  const amount = collapsedPeriodHeaderAmount(summary);
  const hasBalanceDue = summary.totalDue > 0;

  if (hasBalanceDue) {
    return (
      <span className="text-sm font-semibold tabular-nums text-destructive">
        Balance due {formatCurrency(amount)}
      </span>
    );
  }

  return (
    <span className="text-sm font-semibold tabular-nums text-green-600">
      Paid {formatCurrency(amount)}
    </span>
  );
}

function RegistrationPeriodComplianceSectionCollapsible({
  section,
  isFirst,
  userType,
}: {
  section: RegistrationPeriodComplianceSection;
  isFirst: boolean;
  userType: UserType;
}) {
  const [open, setOpen] = useState(isFirst);
  const rowHasBalanceDue = section.summary.totalDue > 0;

  return (
    <div
      className={cn(
        !isFirst && "mt-5 border-t border-border pt-5",
        "space-y-3",
      )}
    >
      <button
        type="button"
        className={cn(
          "flex w-full gap-3 rounded-md text-left outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          rowHasBalanceDue
            ? "text-destructive hover:bg-destructive/10"
            : "hover:bg-muted/40",
        )}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <ChevronDown
          className={cn(
            "mt-0.5 h-4 w-4 shrink-0 transition-transform duration-200",
            rowHasBalanceDue ? "text-destructive" : "text-muted-foreground",
            open && "rotate-180",
          )}
          aria-hidden
        />
        <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <p className="min-w-0 text-sm font-semibold tabular-nums">
            {section.periodStartLabel} - {section.periodEndLabel}
          </p>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 sm:ml-4">
            {!open ? <CollapsedPeriodAmount summary={section.summary} /> : null}
            {section.isCurrentPeriod ? (
              <Badge
                variant={rowHasBalanceDue ? "outline" : "secondary"}
                className={cn(
                  "text-xs font-normal",
                  rowHasBalanceDue &&
                    "border-destructive/50 text-destructive bg-destructive/5",
                )}
              >
                Current period
              </Badge>
            ) : null}
          </div>
        </div>
      </button>

      {open ? (
        <div className="space-y-3 pl-7">
          {!section.hasPayments ? (
            <p className="text-sm text-muted-foreground">
              No payments recorded for this period.
            </p>
          ) : null}
          <PeriodSummaryFigures summary={section.summary} />
          {canDownloadInvoicePdf(userType) &&
          section.invoiceDownloadTargets.length > 0 ? (
            <div className="mt-3 pt-3 border-t flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {section.invoiceDownloadTargets.map((t) => (
                <LicenseInvoiceDownloadButton
                  key={t.manualTransactionId}
                  userType={userType}
                  manualTransactionId={t.manualTransactionId}
                  className="w-full sm:flex-1 sm:min-w-[10rem]"
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Registration fee paid/due by renewal period (compliance), newest period first. */
export function RegistrationCurrentPeriodSummaryCard({
  derived,
  isRefreshing,
  userType,
}: {
  derived: LicensePaymentsDerived;
  /** True while a background full runtime-cache refresh is in flight. */
  isRefreshing?: boolean;
  userType: UserType;
}) {
  const sections = derived.periodComplianceSections;
  if (sections.length === 0) return null;

  return (
    <Card className="gap-4">
      <CardHeader className="flex flex-row items-center gap-2">
        <DetailsTileTitle name="Registration period compliance" />
        {isRefreshing ? (
          <Loader2
            className="h-5 w-5 shrink-0 animate-spin text-muted-foreground"
            aria-label="Updating totals"
          />
        ) : null}
      </CardHeader>
      <CardContent className="space-y-0">
        {sections.map((section, index) => (
          <RegistrationPeriodComplianceSectionCollapsible
            key={section.periodStart}
            section={section}
            isFirst={index === 0}
            userType={userType}
          />
        ))}
      </CardContent>
    </Card>
  );
}
